import { PhpArray } from './ordered-array';
import { PhpFloat } from './values';
import { array_is_list } from './arrays';
import { readItemField, type ItemFieldRow, type ItemFieldDependencies } from '../compile/item-field-reader';
import { createLayerValueReader, type LayerValueDependencies } from '../compile/layer-value-reader';
import { readNodeItemId } from '../compile/layer-cell-readers';

// These existing pure readers understand compiled EC omissions and items wrappers.
// They never turn an unanchored target shell into a complete creature.
export { ecLean } from '../compile/ec-lean-reader';
/** Page readers consume native compiled JSON rows, not ordered PHP storage.
 * Reject the latter explicitly: treating PhpArray's internal properties as a
 * page row would silently turn a present field into null. */
function pageRow(row: ItemFieldRow | null): void {
    if (row instanceof PhpArray || row instanceof PhpFloat) throw new TypeError('Page reader requires native compiled JSON; PhpArray/PhpFloat rows are unsupported');
}
export function itemField(row: ItemFieldRow | null, key: string, dependencies: ItemFieldDependencies = {}): unknown {
    pageRow(row);
    return readItemField(row, key, dependencies);
}
/** External provider/inventory reads belong to the caller's compile context.
 * Missing dependencies throw in the leaf reader; they never imply an empty list. */
export function layerValue(row: ItemFieldRow | null, name: string, dependencies: Omit<LayerValueDependencies, 'nodeItemId'> = {
    providers: () => { const error = new Error('layer_value provider context unavailable'); error.name = 'MissingLayerValueProviders'; throw error; },
}, visited?: ItemFieldRow): unknown {
    pageRow(row);
    return createLayerValueReader({ ...dependencies, nodeItemId: readNodeItemId }).read(row, name, visited);
}
export const JSON_HEX_TAG = 1, JSON_HEX_AMP = 2, JSON_HEX_APOS = 4, JSON_HEX_QUOT = 8;
export const JSON_UNESCAPED_SLASHES = 64, JSON_UNESCAPED_UNICODE = 256, JSON_PARTIAL_OUTPUT_ON_ERROR = 512;
export const JSON_PRESERVE_ZERO_FRACTION = 1024, JSON_UNESCAPED_LINE_TERMINATORS = 2048, JSON_THROW_ON_ERROR = 4194304;

/** Glue around the page's item_field twin, rather than another wrapper reader. */
export function wrappedItemId(value: unknown): unknown {
    let single = value;
    const visited = new Set<unknown>();
    while (Array.isArray(single)) {
        if (visited.has(single)) throw new RangeError('Cyclic item-id wrapper');
        visited.add(single); single = single[0];
    }
    return single !== null && typeof single === 'object' ? itemField(single as ItemFieldRow, 'item_id') : single ?? null;
}

/** A JSON value is decoded as PHP associative arrays. Set text=true for JSON
 * source text: this recursive parser preserves object member order and integer
 * lexemes (JSON.parse and Object.keys cannot recover either after decoding).
 */
export function fromJson(value: unknown, text = false): unknown {
    if (!text) {
        if (value instanceof PhpFloat) return new PhpFloat(value.value);
        if (value instanceof PhpArray) return value.copy(v => fromJson(v));
        if (value !== null && typeof value === 'object') return new PhpArray(Object.entries(value).map(([key, v]) => [key, fromJson(v)]));
        return value;
    }
    if (typeof value !== 'string') throw new TypeError('JSON text must be a string');
    let at = 0;
    const ws = (): void => { while (/[\t\r\n ]/.test(value[at] ?? '') && at < value.length) at++; };
    const string = (): string => {
        const start = at++;
        while (at < value.length) {
            const c = value[at++];
            if (c === '\\') at++;
            else if (c === '"') return JSON.parse(value.slice(start, at)) as string;
        }
        throw new SyntaxError('Unterminated JSON string');
    };
    const parse = (): unknown => {
        ws(); const c = value[at];
        if (c === '"') return string();
        if (c === '[' || c === '{') {
            at++; ws(); const result = new PhpArray(), end = c === '[' ? ']' : '}';
            if (value[at] === end) { at++; return result; }
            while (true) {
                ws(); let key: string | undefined;
                if (c === '{') {
                    if (value[at] !== '"') throw new SyntaxError('Expected JSON key');
                    key = string(); ws();
                    if (value[at++] !== ':') throw new SyntaxError('Expected JSON colon');
                }
                const entry = parse();
                if (key === undefined) result.append(entry); else result.set(key, entry);
                ws(); const next = value[at++];
                if (next === end) return result;
                if (next !== ',') throw new SyntaxError('Expected JSON comma');
            }
        }
        for (const [literal, decoded] of [['null', null], ['true', true], ['false', false]] as const) {
            if (value.slice(at, at + literal.length) === literal) { at += literal.length; return decoded; }
        }
        const token = value.slice(at).match(/^-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?/)?.[0];
        if (!token) throw new SyntaxError('Invalid JSON value');
        at += token.length;
        if (!/[.eE]/.test(token)) {
            const n = BigInt(token);
            if (n >= -9223372036854775808n && n <= 9223372036854775807n) return n >= BigInt(Number.MIN_SAFE_INTEGER) && n <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(n) : n;
        }
        return new PhpFloat(Number(token));
    };
    const result = parse(); ws();
    if (at !== value.length) throw new SyntaxError('Trailing JSON input');
    return result;
}
function encodeString(value: string, flags: number): string {
    // UUIDs, field names and ordinary ASCII text need no per-character codec.
    // Keep every escape/flag-sensitive character on the exact path below.
    if (!/[^\x20-\x7e]|["\\\/<>&']/.test(value)) return '"' + value + '"';
    let encoded = '"';
    // Encode source characters, never the delimiters/escapes of serialized JSON.
    for (const c of value) {
        const hex = c === '<' || c === '>' ? flags & JSON_HEX_TAG : c === '&' ? flags & JSON_HEX_AMP
            : c === "'" ? flags & JSON_HEX_APOS : c === '"' ? flags & JSON_HEX_QUOT : 0;
        if (hex) encoded += '\\u' + c.charCodeAt(0).toString(16).padStart(4, '0').toUpperCase();
        else if (c === '/' && !(flags & JSON_UNESCAPED_SLASHES)) encoded += '\\/';
        else if (!(flags & JSON_UNESCAPED_UNICODE) && c.charCodeAt(0) >= 128
            || !(flags & JSON_UNESCAPED_LINE_TERMINATORS) && /[\u2028\u2029]/.test(c)) {
            for (let i = 0; i < c.length; i++) encoded += '\\u' + c.charCodeAt(i).toString(16).padStart(4, '0');
        } else encoded += JSON.stringify(c).slice(1, -1);
    }
    return encoded + '"';
}
function encodeFloat(n: number, flags: number): string {
    if (!Number.isFinite(n)) {
        if (flags & JSON_PARTIAL_OUTPUT_ON_ERROR) return '0';
        throw new RangeError('Inf and NaN cannot be JSON encoded');
    }
    if (Object.is(n, -0)) return flags & JSON_PRESERVE_ZERO_FRACTION ? '-0.0' : '-0';
    const raw = n.toString(), exponent = n === 0 ? 0 : Number(Math.abs(n).toExponential().split('e')[1]);
    if (exponent < -4 || exponent >= 17) {
        const scientific = Number(raw).toExponential().replace(/e([+-])0+(\d+)/, 'e$1$2');
        return scientific.replace(/^(-?\d)(e)/, '$1.0$2');
    }
    const plain = /e/.test(raw) ? n.toFixed(Math.max(0, -exponent) + 16).replace(/0+$/, '').replace(/\.$/, '') : raw;
    return flags & JSON_PRESERVE_ZERO_FRACTION && !plain.includes('.') ? plain + '.0' : plain;
}
/** PHP json_encode for associative arrays, precision=-1. Returns false on errors
 * unless JSON_THROW_ON_ERROR is selected, as PHP does. Unknown flag bits are rejected.
 */
export function json_encode(value: unknown, flags = 0): string | false {
    const supported = 1 | 2 | 4 | 8 | 64 | 256 | 512 | 1024 | 2048 | 4194304;
    if (flags & ~supported) throw new RangeError('Unsupported json_encode flag');
    const ancestors = new Set<object>();
    const encode = (v: unknown): string => {
        if (v == null) return 'null';
        if (typeof v === 'string') return encodeString(v, flags);
        if (typeof v === 'boolean') return v ? 'true' : 'false';
        if (typeof v === 'bigint') return String(v);
        if (typeof v === 'number') return Number.isSafeInteger(v) ? String(v) : encodeFloat(v, flags);
        if (v instanceof PhpFloat) return encodeFloat(v.value, flags);
        if (typeof v !== 'object') throw new TypeError('Unsupported PHP JSON value');
        if (ancestors.has(v)) {
            if (flags & JSON_PARTIAL_OUTPUT_ON_ERROR) return 'null';
            throw new TypeError('Recursion detected');
        }
        ancestors.add(v);
        const pairs = v instanceof PhpArray ? [...v] : Object.entries(v);
        const list = v instanceof PhpArray ? array_is_list(v) : pairs.every(([key], index) => key === String(index));
        const content = pairs.map(([key, entry]) => (list ? '' : encodeString(String(key), flags) + ':') + encode(entry)).join(',');
        ancestors.delete(v);
        return list ? '[' + content + ']' : '{' + content + '}';
    };
    try { return encode(value); } catch (error) {
        if (flags & JSON_THROW_ON_ERROR) { if (error instanceof Error) error.name = 'JsonException'; throw error; }
        return false;
    }
}
export const toJson = json_encode;

/** Value of JSON.parse(json_encode(value)) without constructing JSON text.
 * Default flags change string escaping, not decoded strings. Numeric strings
 * stay strings; empty/key-contiguous PHP arrays become lists. Plain-object keys
 * already have the spelling normalizeKey would emit, so no PhpArray allocation
 * is needed. Every occurrence is copied (JSON decoding never retains aliases).
 */
export function nativePhpJson(value: unknown): unknown {
    const ancestors = new Set<object>();
    function convert(input: unknown): unknown {
        if (input == null) return null;
        if (typeof input === 'string' || typeof input === 'boolean') return input;
        if (typeof input === 'bigint') return Number(input);
        if (typeof input === 'number' || input instanceof PhpFloat) {
            const number = input instanceof PhpFloat ? input.value : input;
            if (!Number.isFinite(number)) throw new TypeError('PHP JSON unavailable');
            // json_encode uses String for safe integer JS numbers (including
            // -0); tagged PHP doubles take encodeFloat's signed-zero branch.
            return !(input instanceof PhpFloat) && number === 0 ? 0 : number;
        }
        if (typeof input !== 'object' || ancestors.has(input)) throw new TypeError('PHP JSON unavailable');
        ancestors.add(input);
        let result: unknown;
        if (input instanceof PhpArray) {
            const pairs = [...input];
            if (pairs.every(([key], index) => key === index)) result = pairs.map(([, entry]) => convert(entry));
            else {
                const row: Record<string, unknown> = {};
                for (const [key, entry] of pairs) {
                    const name = String(key), value = convert(entry);
                    if (name === '__proto__') Object.defineProperty(row, name, { value, enumerable: true, writable: true, configurable: true });
                    else row[name] = value;
                }
                result = row;
            }
        } else {
            const keys = Object.keys(input), list = keys.every((key, index) => key === String(index));
            if (list) {
                const rows: unknown[] = [];
                for (const key of keys) rows.push(convert((input as Record<string, unknown>)[key]));
                result = rows;
            } else {
                const row: Record<string, unknown> = {};
                for (const key of keys) {
                    const value = convert((input as Record<string, unknown>)[key]);
                    if (key === '__proto__') Object.defineProperty(row, key, { value, enumerable: true, writable: true, configurable: true });
                    else row[key] = value;
                }
                result = row;
            }
        }
        ancestors.delete(input);
        return result;
    }
    return convert(value);
}
